import { useMemo, type ReactNode } from "react";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { PageBackButton } from "@/shared/components/PageBackButton";
import { PageSpinner } from "@/shared/components/spinner";
import { adaptMedication } from "@/features/patient/medications/utils/adaptMedication";
import { ISSUE_LABELS } from "@/features/patient/medications/components/medicationReviewBadges";
import { ReviewRecords, isPairItem } from "@/features/patient/medications/components/ReviewConflictCard";
import type { QueueItemPreview } from "@/portals/clinician/api/reviewQueueApi";
import { RxNormBlock } from "./reviewBadges";

/**
 * The review-item screen's layout, shared by every portal that shows one item:
 * the clinician's held item and queue preview, and the admin's queue view.
 * Header (back, identity, badges), the issue and the records with RxNorm; the
 * Patient Conversation (held items only — view-only screens have none) and the
 * side panel (decision / Claim / Assign) are left to the caller. Data loading and live refresh stay with each caller, since
 * each portal has its own API and socket.
 */

const BRAND_BUTTON =
  "!h-10 !rounded-lg !border-transparent !bg-[#E4002B]/50 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white";

export function formatDate(value: string) {
  // yyyy-mm-dd is a calendar date — build it locally so it never shifts a day by timezone.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date = dateOnly ? new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]) : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const nonEmpty = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

// Records carry the full MedicationRequest field set (same keys as /api/patient/records),
// so they go through the patient adapter unchanged: dose_quantity/dose_unit,
// frequency, dosage_text, requester_ref, authored_on. The one clinician-only
// rule: a missing or empty authored_on falls back to the record's createdAt,
// never to the adapter's 1970 placeholder (patient screens unchanged).
function toMedications(item: QueueItemPreview) {
  return item.records.map((r) =>
    adaptMedication({
      id: r.id, resourceType: "MedicationRequest", sourceVendor: r.sourceVendor, uploadId: null,
      confidenceScore: 1, needsReview: false, reviewReason: null, createdAt: r.createdAt, updatedAt: r.createdAt,
      fields: { ...r.fields, authored_on: nonEmpty(r.fields.authored_on) || r.createdAt },
      review: { status: "UNDER_REVIEW", reviewItemId: item.id, recordRole: r.recordRole, triggerType: item.triggerType },
      origin: null,
    }),
  );
}

/** The shared "←" back control (sits in the header row instead of taking a row of its own). */
function BackArrow({ onClick, label }: { onClick: () => void; label: string }) {
  return <PageBackButton onClick={onClick} ariaLabel={`Back to ${label}`} />;
}

/** Name + date of birth, for the header's identity slot. */
export function PatientIdentity({ patient }: { patient: { displayName: string; dateOfBirth: string | null } }) {
  return (
    <div>
      <p className="text-lg font-extrabold text-[#0A2947]">{patient.displayName}</p>
      <p className="text-xs text-[#6B7280]">Date of birth {patient.dateOfBirth ? formatDate(patient.dateOfBirth) : "not on file"}</p>
    </div>
  );
}

/** Loading, or a full-screen message (not found / no longer available / failed) with a way back. */
export function ReviewItemScreenMessage({
  loading,
  icon,
  message,
  backLabel,
  onBack,
}: {
  loading?: boolean;
  icon?: string;
  message?: string;
  backLabel: string;
  onBack: () => void;
}) {
  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      <div className="mb-3"><BackArrow onClick={onBack} label={backLabel} /></div>
      {loading ? (
        <PageSpinner className="bg-transparent" />
      ) : (
        <div className="mx-auto flex max-w-xl flex-col items-center rounded-3xl border border-[#E5E7EB] bg-white px-6 py-12 text-center shadow-sm" role="status">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#F3F4F6]">
            <Icon icon={icon ?? "lucide:file-x"} className="h-7 w-7 text-[#9CA3AF]" />
          </div>
          <p className="mt-4 text-base font-extrabold text-[#0A2947]">{message}</p>
          <Button onClick={onBack} className={`!mt-6 !px-6 ${BRAND_BUTTON}`}>
            Back to {backLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

/** Side panel for a view-only screen: what this view is, and its one action (Claim / Assign). */
export function ViewOnlyActionPanel({
  note,
  actionLabel,
  busy,
  onAction,
  testId,
}: {
  note: string;
  actionLabel: string;
  busy?: boolean;
  onAction: () => void;
  testId?: string;
}) {
  return (
    <div className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm" data-testid={testId}>
      <div className="flex items-start gap-2.5">
        <Icon icon="lucide:eye" className="mt-0.5 h-4 w-4 shrink-0 text-[#6B7280]" />
        <p className="text-sm text-[#4B5563]">{note}</p>
      </div>
      <Button busy={busy} onClick={onAction} className={`!mt-4 !w-full ${BRAND_BUTTON}`}>
        {actionLabel}
      </Button>
    </div>
  );
}

interface ReviewItemLayoutProps {
  item: QueueItemPreview;
  backLabel: string;
  onBack: () => void;
  /** Who the patient is (or why they're hidden). */
  identity: ReactNode;
  /** Status / attachment badges, top right. */
  badges: ReactNode;
  /** The side panel: decision, Claim or Assign. */
  aside: ReactNode;
  /** Below the records — the Patient Conversation on a held item; omitted on view-only screens. */
  renderConversation?: (isPair: boolean) => ReactNode;
}

export function ReviewItemLayout({ item, backLabel, onBack, identity, badges, aside, renderConversation }: ReviewItemLayoutProps) {
  const meds = useMemo(() => toMedications(item), [item]);
  const rxById = useMemo(() => new Map(item.records.map((r) => [r.id, r.rxnorm])), [item]);
  // Instructions and prescriber, read from the same keys the patient adapter uses
  // (dosage_text, requester_ref) — "Not specified" only when actually missing/empty.
  const extrasById = useMemo(
    () =>
      new Map(
        item.records.map((r) => [
          r.id,
          {
            instructions: nonEmpty(r.fields.dosage_text),
            prescriber: nonEmpty(r.fields.requester_ref).replace(/^Practitioner\//, ""),
          },
        ]),
      ),
    [item],
  );
  const isPair = isPairItem(meds);

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      {/* Side panel beside the records only when there's room (xl); below it on iPad. */}
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="grid min-w-0 content-start gap-6 rounded-3xl border border-[#E5E7EB] bg-white p-4 shadow-sm sm:p-6">
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <BackArrow onClick={onBack} label={backLabel} />
              {identity}
            </div>
            <div className="flex flex-wrap items-center gap-2">{badges}</div>
          </div>
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#E4002B]/50">
              <Icon icon={isPair ? "lucide:git-compare" : "lucide:circle-help"} className="h-4 w-4 !text-white" />
            </div>
            <h2 className="text-base font-extrabold text-[#0A2947]">{ISSUE_LABELS[item.triggerType]}</h2>
          </div>

          {/* Records + RxNorm */}
          <ReviewRecords
            records={meds}
            renderExtra={(med) => (
              <>
                {[
                  { label: "Instructions", value: extrasById.get(med.id)?.instructions, testId: "record-instructions" },
                  { label: "Prescriber", value: extrasById.get(med.id)?.prescriber, testId: "record-prescriber" },
                ].map(({ label, value, testId }) => (
                  <div key={label} className="mt-2 flex items-baseline justify-between gap-3" data-testid={testId}>
                    <span className="text-xs text-[#9CA3AF]">{label}</span>
                    <span className="text-right text-sm font-semibold text-[#0A2947]">{value || "Not specified"}</span>
                  </div>
                ))}
                <RxNormBlock match={rxById.get(med.id) ?? null} />
              </>
            )}
          />

          {renderConversation?.(isPair)}
        </div>

        <aside className="grid content-start gap-4">{aside}</aside>
      </div>
    </div>
  );
}
